import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, Truck, User, MapPin, Phone, Mail } from 'lucide-react';
import { storeOrderInFirebase, updateProductStock } from '../db/firebaseOrders';
import emailjs from '@emailjs/browser';

const Checkout = () => {
    const navigate = useNavigate();
    const [cartItems, setCartItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [orderProcessing, setOrderProcessing] = useState(false);

    // Form states
    const [personalInfo, setPersonalInfo] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        alternatePhone: ''
    });

    const [addressInfo, setAddressInfo] = useState({
        streetAddress: '',
        apartment: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'Pakistan',
        landmark: ''
    });

    const [paymentMethod, setPaymentMethod] = useState('cod');
    const [orderNotes, setOrderNotes] = useState('');

    // Form validation states
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    // EmailJS configuration
    const EMAILJS_CONFIG = {
        SERVICE_ID: 'service_leatherhood',
        TEMPLATE_ID: 'template_gkxgt6j',
        PUBLIC_KEY: 'iW13kdoZPem2XJyF6'
    };

    // Company information
    const COMPANY_INFO = {
        name: 'LeatherHood PK',
        email: 'help@leatherhood.pk',
        phone: '+92 333 3112200'
    };

    // Load cart items from localStorage
    useEffect(() => {
        const loadCart = () => {
            const savedCart = localStorage.getItem('shoppingCart');
            if (savedCart) {
                try {
                    const parsedCart = JSON.parse(savedCart);
                    setCartItems(parsedCart);
                } catch (error) {
                    console.error('Error loading cart:', error);
                    setCartItems([]);
                }
            } else {
                setCartItems([]);
            }
            setLoading(false);
        };

        loadCart();
    }, []);

    // Calculate order totals
    const calculateSubtotal = () => {
        return cartItems.reduce((total, item) => {
            const price = item.productDiscountPrice || item.productPrice;
            return total + (price * item.productQuantity);
        }, 0);
    };

    const calculateShipping = () => {
        return 0; // Free shipping
    };

    const calculateTotal = () => {
        return calculateSubtotal() + calculateShipping();
    };

    const calculateTotalItems = () => {
        return cartItems.reduce((total, item) => total + item.productQuantity, 0);
    };

    // Form handlers
    const handlePersonalInfoChange = (field, value) => {
        setPersonalInfo(prev => ({
            ...prev,
            [field]: value
        }));

        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleAddressInfoChange = (field, value) => {
        setAddressInfo(prev => ({
            ...prev,
            [field]: value
        }));

        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleBlur = (field) => {
        setTouched(prev => ({
            ...prev,
            [field]: true
        }));
    };

    // Validation functions
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhone = (phone) => {
        const phoneRegex = /^03[0-9]{2}[0-9]{7}$/;
        return phoneRegex.test(phone);
    };

    const validateForm = () => {
        const newErrors = {};

        // Personal info validation
        if (!personalInfo.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }
        if (!personalInfo.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }
        if (!personalInfo.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!validateEmail(personalInfo.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (!personalInfo.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        } else if (!validatePhone(personalInfo.phone)) {
            newErrors.phone = 'Please enter a valid Pakistani phone number (03XXXXXXXXX)';
        }

        // Address validation
        if (!addressInfo.streetAddress.trim()) {
            newErrors.streetAddress = 'Street address is required';
        }
        if (!addressInfo.city.trim()) {
            newErrors.city = 'City is required';
        }
        if (!addressInfo.state.trim()) {
            newErrors.state = 'State/Province is required';
        }
        if (!addressInfo.zipCode.trim()) {
            newErrors.zipCode = 'ZIP code is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Format order items for email
    const formatOrderItemsForEmail = (items) => {
        return items.map(item => {
            const price = item.productDiscountPrice || item.productPrice;
            const totalPrice = price * item.productQuantity;
            return `${item.productName} (${item.productColor}, ${item.productSize}, ${item.productSoleType || 'Leather'}) - Qty: ${item.productQuantity} - PKR ${totalPrice.toLocaleString()}`;
        }).join('; ');
    };

    // Format shipping address for email
    const formatShippingAddressForEmail = (address, personal) => {
        return `${address.streetAddress}${address.apartment ? ', ' + address.apartment : ''}, ${address.city}, ${address.state} ${address.zipCode}, ${address.country}${address.landmark ? ' (Near: ' + address.landmark + ')' : ''}`;
    };

    // Send order confirmation email using EmailJS
    const sendOrderConfirmationEmail = async (order, firebaseOrderId) => {
        try {
            // Prepare template parameters
            const templateParams = {
                order_id: order.orderId,
                customer_name: `${personalInfo.firstName} ${personalInfo.lastName}`,
                order_date: new Date(order.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                }),
                payment_method: order.paymentMethod === 'cod' ? 'Cash on Delivery' : 'Online Payment',
                total_items: calculateTotalItems().toString(),
                shipping_address: formatShippingAddressForEmail(order.shippingAddress, order.customer),
                order_items: formatOrderItemsForEmail(order.items),
                order_notes: order.notes || 'No special instructions',
                subtotal: `PKR ${order.subtotal.toLocaleString()}`,
                shipping: `PKR ${order.shipping.toLocaleString()}`,
                total: `PKR ${order.total.toLocaleString()}`,
                company_phone: COMPANY_INFO.phone,
                company_name: COMPANY_INFO.name,
                company_email: COMPANY_INFO.email,
                customer_email: personalInfo.email
            };

            console.log('Sending email with parameters:', templateParams);

            // Send email using EmailJS
            const result = await emailjs.send(
                EMAILJS_CONFIG.SERVICE_ID,
                EMAILJS_CONFIG.TEMPLATE_ID,
                templateParams,
                EMAILJS_CONFIG.PUBLIC_KEY
            );

            console.log('Email sent successfully:', result);
            return true;
        } catch (error) {
            console.error('Failed to send order confirmation email:', error);
            // Don't throw error here - we don't want email failure to block order placement
            return false;
        }
    };

    // Place order function with Firebase integration and EmailJS
    const handlePlaceOrder = async () => {
        // Mark all fields as touched
        const allTouched = {};
        Object.keys(personalInfo).forEach(key => allTouched[key] = true);
        Object.keys(addressInfo).forEach(key => allTouched[key] = true);
        setTouched(allTouched);

        if (!validateForm()) {
            alert('Please fix the errors in the form before proceeding.');
            return;
        }

        if (cartItems.length === 0) {
            alert('Your cart is empty. Please add items before placing an order.');
            return;
        }

        setOrderProcessing(true);

        try {
            // Create order object
            const order = {
                orderId: 'ORD-' + Date.now(),
                date: new Date().toISOString(),
                customer: personalInfo,
                shippingAddress: addressInfo,
                paymentMethod,
                items: cartItems,
                subtotal: calculateSubtotal(),
                shipping: calculateShipping(),
                total: calculateTotal(),
                status: 'confirmed',
                notes: orderNotes,
                timestamp: new Date()
            };

            console.log('Placing order:', order);

            // 1. Store order in Firebase
            const firebaseOrderId = await storeOrderInFirebase(order);
            console.log('Order stored in Firebase with ID:', firebaseOrderId);

            // 2. Update product stock in Firebase
            await updateProductStock(cartItems);
            console.log('Product stock updated successfully');

            // 3. Send order confirmation email
            const emailSent = await sendOrderConfirmationEmail(order, firebaseOrderId);
            if (emailSent) {
                console.log('Order placed email sent to customer');
            } else {
                console.warn('Order placed email failed, but order was placed successfully');
            }

            // 4. Clear cart from localStorage
            localStorage.removeItem('shoppingCart');
            
            // 5. Dispatch cart update event to refresh other components
            window.dispatchEvent(new Event('cartUpdated'));
            
            console.log('Order process completed successfully');

            // 6. Redirect to order confirmation
            navigate('/confirmorder', { 
                state: { 
                    orderId: order.orderId,
                    orderTotal: order.total,
                    firebaseOrderId: firebaseOrderId,
                    emailSent: emailSent
                }
            });

        } catch (error) {
            console.error('Error placing order:', error);
            alert('There was an error placing your order. Please try again. Error: ' + error.message);
        } finally {
            setOrderProcessing(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-beige p-6">
                <div className="max-w-6xl mx-auto">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            <div className="lg:col-span-2 space-y-4">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="h-32 bg-gray-300 rounded"></div>
                                ))}
                            </div>
                            <div className="h-64 bg-gray-300 rounded"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (cartItems.length === 0) {
        return (
            <div className="min-h-screen bg-beige p-6">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="bg-offwhite rounded-lg p-12 shadow-sm">
                        <h1 className="text-3xl font-bold text-charcoal mb-4">No Items in Cart</h1>
                        <p className="text-charcoal/70 mb-8">
                            Your cart is empty. Please add some items before proceeding to checkout.
                        </p>
                        <button
                            onClick={() => navigate('/shop')}
                            className="bg-primary text-offwhite px-8 py-3 rounded-lg hover:bg-primary-dark transition-colors font-semibold"
                        >
                            Continue Shopping
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-beige p-6">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center text-charcoal hover:text-primary transition-colors mb-4"
                    >
                        <ArrowLeft size={20} className="mr-2" />
                        Back to Cart
                    </button>
                    <h1 className="text-3xl font-bold text-charcoal mb-2">Checkout</h1>
                    <p className="text-charcoal/60">
                        Complete your purchase by filling in the details below
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Column - Forms */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Personal Information */}
                        <div className="bg-offwhite rounded-lg p-6 shadow-sm border border-taupe/30">
                            <div className="flex items-center space-x-3 mb-4">
                                <User size={24} className="text-primary" />
                                <h2 className="text-xl font-bold text-charcoal">Personal Information</h2>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-charcoal mb-2">
                                        First Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={personalInfo.firstName}
                                        onChange={(e) => handlePersonalInfoChange('firstName', e.target.value)}
                                        onBlur={() => handleBlur('firstName')}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                                            errors.firstName && touched.firstName 
                                                ? 'border-red-500' 
                                                : 'border-taupe/50'
                                        }`}
                                        placeholder="Enter your first name"
                                    />
                                    {errors.firstName && touched.firstName && (
                                        <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-charcoal mb-2">
                                        Last Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={personalInfo.lastName}
                                        onChange={(e) => handlePersonalInfoChange('lastName', e.target.value)}
                                        onBlur={() => handleBlur('lastName')}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                                            errors.lastName && touched.lastName 
                                                ? 'border-red-500' 
                                                : 'border-taupe/50'
                                        }`}
                                        placeholder="Enter your last name"
                                    />
                                    {errors.lastName && touched.lastName && (
                                        <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-charcoal mb-2">
                                        Email Address *
                                    </label>
                                    <input
                                        type="email"
                                        value={personalInfo.email}
                                        onChange={(e) => handlePersonalInfoChange('email', e.target.value)}
                                        onBlur={() => handleBlur('email')}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                                            errors.email && touched.email 
                                                ? 'border-red-500' 
                                                : 'border-taupe/50'
                                        }`}
                                        placeholder="your@email.com"
                                    />
                                    {errors.email && touched.email && (
                                        <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-charcoal mb-2">
                                        Phone Number *
                                    </label>
                                    <input
                                        type="tel"
                                        value={personalInfo.phone}
                                        onChange={(e) => handlePersonalInfoChange('phone', e.target.value)}
                                        onBlur={() => handleBlur('phone')}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                                            errors.phone && touched.phone 
                                                ? 'border-red-500' 
                                                : 'border-taupe/50'
                                        }`}
                                        placeholder="03XXXXXXXXX"
                                    />
                                    {errors.phone && touched.phone && (
                                        <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-charcoal mb-2">
                                        Alternate Phone (Optional)
                                    </label>
                                    <input
                                        type="tel"
                                        value={personalInfo.alternatePhone}
                                        onChange={(e) => handlePersonalInfoChange('alternatePhone', e.target.value)}
                                        className="w-full px-3 py-2 border border-taupe/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                        placeholder="03XXXXXXXXX"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Shipping Address */}
                        <div className="bg-offwhite rounded-lg p-6 shadow-sm border border-taupe/30">
                            <div className="flex items-center space-x-3 mb-4">
                                <MapPin size={24} className="text-primary" />
                                <h2 className="text-xl font-bold text-charcoal">Shipping Address</h2>
                            </div>
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-charcoal mb-2">
                                        Street Address *
                                    </label>
                                    <input
                                        type="text"
                                        value={addressInfo.streetAddress}
                                        onChange={(e) => handleAddressInfoChange('streetAddress', e.target.value)}
                                        onBlur={() => handleBlur('streetAddress')}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                                            errors.streetAddress && touched.streetAddress 
                                                ? 'border-red-500' 
                                                : 'border-taupe/50'
                                        }`}
                                        placeholder="House #, Street, Area"
                                    />
                                    {errors.streetAddress && touched.streetAddress && (
                                        <p className="text-red-500 text-sm mt-1">{errors.streetAddress}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-charcoal mb-2">
                                        Apartment, Suite, etc. (Optional)
                                    </label>
                                    <input
                                        type="text"
                                        value={addressInfo.apartment}
                                        onChange={(e) => handleAddressInfoChange('apartment', e.target.value)}
                                        className="w-full px-3 py-2 border border-taupe/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                        placeholder="Apartment #, Suite, etc."
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-charcoal mb-2">
                                            City *
                                        </label>
                                        <input
                                            type="text"
                                            value={addressInfo.city}
                                            onChange={(e) => handleAddressInfoChange('city', e.target.value)}
                                            onBlur={() => handleBlur('city')}
                                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                                                errors.city && touched.city 
                                                    ? 'border-red-500' 
                                                    : 'border-taupe/50'
                                            }`}
                                            placeholder="City"
                                        />
                                        {errors.city && touched.city && (
                                            <p className="text-red-500 text-sm mt-1">{errors.city}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-charcoal mb-2">
                                            State/Province *
                                        </label>
                                        <input
                                            type="text"
                                            value={addressInfo.state}
                                            onChange={(e) => handleAddressInfoChange('state', e.target.value)}
                                            onBlur={() => handleBlur('state')}
                                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                                                errors.state && touched.state 
                                                    ? 'border-red-500' 
                                                    : 'border-taupe/50'
                                            }`}
                                            placeholder="State/Province"
                                        />
                                        {errors.state && touched.state && (
                                            <p className="text-red-500 text-sm mt-1">{errors.state}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-charcoal mb-2">
                                            ZIP Code *
                                        </label>
                                        <input
                                            type="text"
                                            value={addressInfo.zipCode}
                                            onChange={(e) => handleAddressInfoChange('zipCode', e.target.value)}
                                            onBlur={() => handleBlur('zipCode')}
                                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                                                errors.zipCode && touched.zipCode 
                                                    ? 'border-red-500' 
                                                    : 'border-taupe/50'
                                            }`}
                                            placeholder="ZIP Code"
                                        />
                                        {errors.zipCode && touched.zipCode && (
                                            <p className="text-red-500 text-sm mt-1">{errors.zipCode}</p>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-charcoal mb-2">
                                        Landmark (Optional)
                                    </label>
                                    <input
                                        type="text"
                                        value={addressInfo.landmark}
                                        onChange={(e) => handleAddressInfoChange('landmark', e.target.value)}
                                        className="w-full px-3 py-2 border border-taupe/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                        placeholder="Nearby landmark for easy delivery"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Payment Method */}
                        <div className="bg-offwhite rounded-lg p-6 shadow-sm border border-taupe/30">
                            <div className="flex items-center space-x-3 mb-4">
                                <CreditCard size={24} className="text-primary" />
                                <h2 className="text-xl font-bold text-charcoal">Payment Method</h2>
                            </div>
                            
                            <div className="space-y-3">
                                <label className="flex items-center space-x-3 p-4 border-2 border-primary bg-primary/5 rounded-lg cursor-pointer">
                                    <input
                                        type="radio"
                                        name="payment"
                                        value="cod"
                                        checked={paymentMethod === 'cod'}
                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                        className="text-primary focus:ring-primary"
                                    />
                                    <Truck size={20} className="text-primary" />
                                    <div>
                                        <div className="font-semibold text-charcoal">Cash on Delivery</div>
                                        <div className="text-sm text-charcoal/60">Pay when you receive your order</div>
                                    </div>
                                </label>
                            </div>
                        </div>

                        {/* Order Notes */}
                        <div className="bg-offwhite rounded-lg p-6 shadow-sm border border-taupe/30">
                            <h2 className="text-xl font-bold text-charcoal mb-4">Order Notes (Optional)</h2>
                            <textarea
                                value={orderNotes}
                                onChange={(e) => setOrderNotes(e.target.value)}
                                className="w-full px-3 py-2 border border-taupe/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                rows="3"
                                placeholder="Any special instructions for your order..."
                            />
                        </div>
                    </div>

                    {/* Right Column - Order Summary */}
                    <div className="lg:col-span-1">
                        <div className="bg-offwhite rounded-lg p-6 shadow-sm border border-taupe/30 sticky top-6">
                            <h2 className="text-xl font-bold text-charcoal mb-4">Order Summary</h2>
                            
                            {/* Order Items */}
                            <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                                {cartItems.map((item, index) => {
                                    const currentPrice = item.productDiscountPrice || item.productPrice;
                                    return (
                                        <div key={index} className="flex items-center space-x-3 pb-3 border-b border-taupe/30 last:border-b-0">
                                            <img
                                                src={item.productImage}
                                                alt={item.productName}
                                                className="w-12 h-12 object-cover rounded border border-taupe/30"
                                                onError={(e) => {
                                                    e.target.src = '/placeholder-image.jpg';
                                                }}
                                            />
                                            <div className="flex-grow">
                                                <div className="font-medium text-charcoal text-sm line-clamp-1">
                                                    {item.productName}
                                                </div>
                                                <div className="text-xs text-charcoal/60">
                                                    {item.productColor} • {item.productSize} • {item.productSoleType || 'Leather'} • Qty: {item.productQuantity}
                                                </div>
                                                {item.soleAdjustment !== 0 && (
                                                    <div className={`text-xs ${item.soleAdjustment < 0 ? 'text-green-600' : 'text-redburnt'}`}>
                                                        Sole adjustment: {item.soleAdjustment > 0 ? '+' : ''}{item.soleAdjustment} PKR
                                                    </div>
                                                )}
                                            </div>
                                            <div className="font-semibold text-charcoal text-sm">
                                                PKR {(currentPrice * item.productQuantity).toLocaleString()}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Order Totals */}
                            <div className="space-y-2 mb-6">
                                <div className="flex justify-between text-charcoal/70">
                                    <span>Subtotal ({calculateTotalItems()} items)</span>
                                    <span>PKR {calculateSubtotal().toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between text-charcoal/70">
                                    <span>Shipping</span>
                                    <span className="text-green-600">Free</span>
                                </div>
                                <div className="border-t border-taupe/30 pt-2">
                                    <div className="flex justify-between text-lg font-bold text-charcoal">
                                        <span>Total</span>
                                        <span>PKR {calculateTotal().toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Place Order Button */}
                            <button
                                onClick={handlePlaceOrder}
                                disabled={orderProcessing}
                                className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                                    orderProcessing
                                        ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                                        : 'bg-primary text-offwhite hover:bg-primary-dark'
                                }`}
                            >
                                {orderProcessing ? 'Processing Order...' : `Place Order - PKR ${calculateTotal().toLocaleString()}`}
                            </button>

                            <p className="text-xs text-charcoal/60 text-center mt-3">
                                By placing your order, you agree to our Terms of Service and Privacy Policy
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Checkout;